import { USERS_URL } from '@constants';
import { EnumLangs, ICreateUserBody, IInviteUserBody, IParams } from '@types';
import { errorHandler, getStorageItem, isEmpty, setStorageItem } from 'helpers';
import { HttpService } from 'services';

import { globalSlice } from '../global';
import { AppDispatch } from '../index';
import { usersSlice } from './usersSlice';

const getUsers = (params?: IParams | null) => {
    const { setIsError, setIsLoading } = globalSlice.actions;
    const { setUsers } = usersSlice.actions;

    const lang = getStorageItem('lang') || EnumLangs.HY;
    const queryString =
        params && !isEmpty(params)
            ? '?' +
              Object.keys(params)
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  .map((key: 'sortType' | 'orderBy' | 'page' | 'limit') => {
                      if (key === 'sortType') return 'mode=' + params[key];
                      if (key === 'orderBy') return 'sort=' + params[key];
                      return key + '=' + params[key];
                  })
                  .join('&')
            : '?';

    return async (dispatch: AppDispatch) => {
        dispatch(setIsLoading(true));

        try {
            const url = `${USERS_URL}${queryString}&translation=${lang}`;

            const { data } = await HttpService({
                url,
            });

            dispatch(setIsError(false));
            dispatch(setUsers(data));
        } catch (error) {
            errorHandler(error, dispatch);
        }

        dispatch(setIsLoading(false));
    };
};

const validateUser = (body: ICreateUserBody, navigate: (url: string) => void) => {
    const { setIsLoading } = globalSlice.actions;

    return async (dispatch: AppDispatch) => {
        dispatch(setIsLoading(true));

        try {
            const { data } = await HttpService({
                url: `${USERS_URL}/validate`,
                method: 'POST',
                body,
            });

            setStorageItem('access_token', data.access_token);
            setStorageItem('refresh_token', data.refresh_token);
            setStorageItem('menu', data.menu);
            setStorageItem('role', data.user.type);
            navigate(`/admin-panel`);
            return true;
        } catch (error) {
            errorHandler(error, dispatch);
        }

        dispatch(setIsLoading(false));
    };
};

const loginPassenger = (body: ICreateUserBody, navigate: (url: string) => void) => {
    const { setIsLoading } = globalSlice.actions;

    return async (dispatch: AppDispatch) => {
        dispatch(setIsLoading(true));

        try {
            const { data } = await HttpService({
                url: `${USERS_URL}/login`,
                method: 'POST',
                body,
            });

            setStorageItem('access_token', data.access_token);
            setStorageItem('refresh_token', data.refresh_token);
            setStorageItem('menu', data.menu);
            setStorageItem('role', data.user.type);
            navigate(`/delete-me`);
            return true;
        } catch (error) {
            errorHandler(error, dispatch);
        }

        dispatch(setIsLoading(false));
    };
};

const inviteUser = (body: IInviteUserBody) => {
    const { setSnackbar, setIsLoading } = globalSlice.actions;

    return async (dispatch: AppDispatch) => {
        dispatch(setIsLoading(true));

        try {
            await HttpService({
                url: `${USERS_URL}/invite`,
                method: 'POST',
                body,
            });

            dispatch(
                setSnackbar({
                    open: true,
                    message: 'has.been.invited',
                    variant: 'success',
                }),
            );

            dispatch(getUsers({ page: 0, limit: 10, roles: 'ADMIN&roles=OPERATOR' }));
            return true;
        } catch (error) {
            errorHandler(error, dispatch);
        }

        dispatch(setIsLoading(false));
    };
};

const deleteUser = (id: string, name: string) => {
    const { setSnackbar, setIsLoading } = globalSlice.actions;

    return async (dispatch: AppDispatch) => {
        dispatch(setIsLoading(true));

        try {
            await HttpService({
                url: `${USERS_URL}/${id}`,
                method: 'DELETE',
            });

            dispatch(
                setSnackbar({
                    open: true,
                    message: 'has.been.deleted',
                    variant: 'success',
                    fullName: name,
                }),
            );
            return true;
        } catch (error) {
            errorHandler(error, dispatch);
        }

        dispatch(setIsLoading(false));
    };
};

const getUserById = (id: string) => {
    const { setIsError, setIsLoading } = globalSlice.actions;
    const { setUser } = usersSlice.actions;

    return async (dispatch: AppDispatch) => {
        dispatch(setIsLoading(true));

        try {
            const url = `${USERS_URL}/${id}`;

            const { data } = await HttpService({
                url,
            });

            dispatch(setIsError(false));
            dispatch(setUser(data));
        } catch (error) {
            errorHandler(error, dispatch);
        }

        dispatch(setIsLoading(false));
    };
};

export const usersOp = {
    validateUser,
    getUsers,
    deleteUser,
    getUserById,
    inviteUser,
    loginPassenger,
};
